/**
 * Web-specific types for Liability Waiver System
 * Copied from app-shared to avoid webpack import restrictions
 */

export type WaiverFieldType = 'signature' | 'initials' | 'date' | 'signer_name' | 'student_name' | 'email' | 'phone' | 'gym_info' | 'text';

export type WaiverSignMethod = 'in_app' | 'uploaded' | 'parent_signed';

export type WaiverTemplateType = 'pdf' | 'html';

export type WaiverPlaceholder =
  | 'gym_name' | 'gym_address' | 'member_name' | 'signer_name'
  | 'student_name' | 'current_date' | 'user_email' | 'user_phone';

export const WAIVER_PLACEHOLDERS: { key: WaiverPlaceholder; label: string; description: string }[] = [
  { key: 'gym_name', label: 'Gym Name', description: 'The name of the gym/business' },
  { key: 'gym_address', label: 'Gym Address', description: 'Full address of the gym' },
  { key: 'member_name', label: 'Member Name', description: 'Full name of the signing member' },
  { key: 'signer_name', label: 'Signer Name', description: 'Name of the person signing (may be parent)' },
  { key: 'student_name', label: 'Student Name', description: 'Name of the student/child' },
  { key: 'current_date', label: 'Current Date', description: 'Date of signing' },
  { key: 'user_email', label: 'Email', description: 'Email of the signing member' },
  { key: 'user_phone', label: 'Phone', description: 'Phone number of the signing member' },
];

export interface WaiverTemplateFieldMeta {
  label?: string;
  font?: string;
  fontSize?: number;
  autoFillSource?: 'user_name' | 'signer_name' | 'student_name' | 'user_email' | 'user_phone' | 'current_date' | 'gym_name' | 'gym_address' | 'custom';
  validationRules?: {
    required?: boolean;
    format?: string;
    minLength?: number;
    maxLength?: number;
  };
  [key: string]: unknown;
}

export interface DamageWaiverTemplate {
  id: string;
  company_id: string;
  name: string;
  description: string | null;
  storage_path: string | null;
  template_type: WaiverTemplateType;
  content_html: string | null;
  is_active: boolean;
  requires_child: boolean;
  created_at: string;
  updated_at: string;
}

export interface DamageWaiverTemplateInsert {
  id?: string;
  company_id: string;
  name: string;
  description?: string | null;
  storage_path?: string | null;
  template_type?: WaiverTemplateType;
  content_html?: string | null;
  is_active?: boolean;
  requires_child?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface DamageWaiverTemplateUpdate {
  id?: string;
  company_id?: string;
  name?: string;
  description?: string | null;
  storage_path?: string | null;
  template_type?: WaiverTemplateType;
  content_html?: string | null;
  is_active?: boolean;
  requires_child?: boolean;
  updated_at?: string;
}

export interface DefaultWaiverTemplate {
  id: string;
  version: string;
  name: string;
  content_html: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DefaultWaiverTemplateInsert {
  id?: string;
  version: string;
  name?: string;
  content_html: string;
  is_active?: boolean;
  created_by?: string | null;
}

export interface DamageWaiverTemplateField {
  id: string;
  template_id: string;
  field_type: WaiverFieldType;
  x: number;
  y: number;
  width: number;
  height: number;
  meta: WaiverTemplateFieldMeta;
  optional: boolean;
  order: number;
  created_at: string;
  updated_at: string;
}

export interface DamageWaiverTemplateFieldInsert {
  id?: string;
  template_id: string;
  field_type: WaiverFieldType;
  x: number;
  y: number;
  width: number;
  height: number;
  meta?: WaiverTemplateFieldMeta;
  optional?: boolean;
  order?: number;
  created_at?: string;
  updated_at?: string;
}

export interface DamageWaiverTemplateFieldUpdate {
  id?: string;
  template_id?: string;
  field_type?: WaiverFieldType;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  meta?: WaiverTemplateFieldMeta;
  optional?: boolean;
  order?: number;
  updated_at?: string;
}

export interface DamageWaiverSignature {
  id: string;
  template_id: string;
  company_id: string;
  user_id: string | null;
  child_profile_id: string | null;
  signed_pdf_path: string;
  signed_at: string;
  signed_by: string;
  sign_method: WaiverSignMethod;
  created_at: string;
}

export interface DamageWaiverSignatureInsert {
  id?: string;
  template_id: string;
  company_id: string;
  user_id?: string | null;
  child_profile_id?: string | null;
  signed_pdf_path: string;
  signed_at?: string;
  signed_by: string;
  sign_method: WaiverSignMethod;
  created_at?: string;
}

export interface DamageWaiverTemplateWithFields extends DamageWaiverTemplate {
  fields: DamageWaiverTemplateField[];
}

export interface WaiverRequirementCheck {
  isRequired: boolean;
  templateId: string | null;
  template: DamageWaiverTemplate | null;
  message?: string;
}

export interface SignWaiverInput {
  templateId: string;
  companyId: string;
  userId?: string;
  childProfileId?: string;
  signatureData: string; // Base64 image or data URI
  signedBy: string;
  signMethod: WaiverSignMethod;
  signedPdfPath?: string; // Optional - if provided, use this instead of generating
  autoFilledFields?: Record<string, string>; // Map of field IDs to values
}

export interface WaiverUploadInput {
  file: File | Blob | { uri: string; type?: string; name?: string };
  companyId: string;
  name: string;
  description?: string;
  requiresChild?: boolean;
}





